'use client'

import { isActivePathname, titleCaseToCamelCase, useSettings } from '@cloud/shared/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from '@tanstack/react-router'
import { useLocale, useLocaleDirection, useTranslations } from 'gt-react'
import { Box, Building2, Check, ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { useCallback, useState } from 'react'
import { DynamicIcon } from '@/components/dynamic-icon'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  Sidebar as SidebarWrapper,
  useSidebar,
} from '@/components/ui/sidebar'
import { moduleNavigationsData } from '@/data/module-navigations'
import { navigationsData } from '@/data/navigations'
import { orpc } from '@/lib/orpcClient'
import { useModulesContext } from '@/providers/modules-provider'
import { useUser } from '@/providers/user-provider'
import type { NavigationNestedItem, NavigationRootItem } from '@/types'
import { CommandMenu } from './command-menu'

export function Sidebar() {
  const t = useTranslations()
  const pathname = useLocation()
  const { openMobile, setOpenMobile, isMobile } = useSidebar()
  const { settings } = useSettings()
  const locale = useLocale()
  const direction = useLocaleDirection(locale) // e.g. "rtl" for "right-to-left"
  const isRTL = direction === 'rtl'
  const { current } = useUser()
  const { isModuleEnabled, isLoading: modulesLoading } = useModulesContext()

  // Workspace switcher: list all workspaces the current user has
  // access to (from the cross-tenant erp_user_tenants index). If the
  // user is in more than one workspace, the sidebar shows a switcher
  // dropdown at the top.
  const { data: workspaceData } = useQuery({
    ...orpc.user.workspaces.queryOptions(),
    retry: false,
    throwOnError: false,
  })

  // Persisted collapse state for sidebar groups. Stored in localStorage
  // so sections stay collapsed/expanded across page navigations.
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-collapsed')
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      try {
        localStorage.setItem('sidebar-collapsed', JSON.stringify(next))
      } catch {}
      return next
    })
  }, [])

  // Custom modules are loaded directly (not through the static registry)
  // so the sidebar shows anything a tenant has built or installed from
  // the marketplace. Silently fails on pre-migration tenants or users
  // with no custom-module grants.
  const { data: customModules } = useQuery({
    ...orpc.modules.custom.modules.list.queryOptions(),
    retry: false,
    throwOnError: false,
  })

  // Nav items for every module in one batch call — avoids N+1. Used to
  // show collapsible sub-entries beneath each custom module.
  const { data: navGroups } = useQuery({
    ...orpc.modules.custom.nav.listForSidebar.queryOptions(),
    retry: false,
    throwOnError: false,
  })
  type SidebarNavItem = NonNullable<typeof navGroups>[number]['items'][number]
  const navItemsByModule = new Map<string, SidebarNavItem[]>()
  for (const group of navGroups ?? []) {
    navItemsByModule.set(group.moduleKey, group.items)
  }

  const isHoizontalAndDesktop = settings.layout === 'horizontal' && !isMobile

  // If the layout is horizontal and not on mobile, don't render the sidebar. (We use a menubar for horizontal layout navigation.)
  if (isHoizontalAndDesktop) return null

  // Helper to check if a nav item should be shown based on module status
  const shouldShowNavItem = (item: NavigationRootItem | NavigationNestedItem): boolean => {
    // If no moduleKey, always show
    if (!('moduleKey' in item) || !item.moduleKey) {
      return true
    }
    // If moduleKey is set, check if module is enabled
    return isModuleEnabled(item.moduleKey)
  }

  // Filter navigation items based on module status
  const filterNavItems = <T extends NavigationRootItem | NavigationNestedItem>(items: T[]): T[] => {
    return items.filter(shouldShowNavItem)
  }

  const renderMenuItem = (item: NavigationRootItem | NavigationNestedItem) => {
    const title = t(`navigation.${titleCaseToCamelCase(item.title)}`)
    const label = item.label && t(`label.${titleCaseToCamelCase(item.label)}`)

    // If the item has nested items, render it with a collapsible dropdown.
    if (item.items) {
      return (
        <Collapsible className="group/collapsible">
          <CollapsibleTrigger asChild>
            <SidebarMenuButton className="w-full justify-between [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center">
                {'iconName' in item && (
                  <DynamicIcon name={item.iconName} className="me-2 h-4 w-4" />
                )}
                <span>{title}</span>
                {'label' in item && (
                  <Badge variant="secondary" className="me-2">
                    {label}
                  </Badge>
                )}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            <SidebarMenuSub>
              {item.items.map((subItem: NavigationNestedItem) => (
                <SidebarMenuItem key={subItem.title}>{renderMenuItem(subItem)}</SidebarMenuItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      )
    }

    // Otherwise, render the item with a link.
    if ('href' in item) {
      const localizedPathname = item.href
      const isActive = isActivePathname(localizedPathname, pathname.pathname)

      return (
        <SidebarMenuButton isActive={isActive} onClick={() => setOpenMobile(!openMobile)} asChild>
          <Link to={localizedPathname}>
            {'iconName' in item && <DynamicIcon name={item.iconName} className="h-4 w-4" />}
            <span>{title}</span>
            {'label' in item && <Badge variant="secondary">{label}</Badge>}
          </Link>
        </SidebarMenuButton>
      )
    }
  }

  return (
    <SidebarWrapper side={isRTL ? 'right' : 'left'}>
      <SidebarHeader>
        {workspaceData && workspaceData.workspaces.length > 1 && (
          <WorkspaceSwitcher
            current={workspaceData.current}
            workspaces={workspaceData.workspaces}
          />
        )}
        <CommandMenu buttonClassName="max-w-full" />
      </SidebarHeader>
      <ScrollArea>
        <SidebarContent className="gap-0">
          {/* Module-specific navigation - only show when modules are loaded */}
          {!modulesLoading &&
            moduleNavigationsData.map((nav) => {
              // Filter items based on module status
              const filteredItems = filterNavItems(nav.items)

              // Skip rendering if no items are visible
              if (filteredItems.length === 0) {
                return null
              }

              const title = t(`navigation.${titleCaseToCamelCase(nav.title)}`)

              const groupKey = nav.title
              const isCollapsed = collapsedGroups[groupKey] ?? false

              return (
                <SidebarGroup key={nav.title}>
                  <Collapsible open={!isCollapsed} onOpenChange={() => toggleGroup(groupKey)}>
                    <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                      <span>{title}</span>
                      {isCollapsed ? (
                        <ChevronRight className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarGroupContent>
                        <SidebarMenu>
                          {filteredItems.map((item) => (
                            <SidebarMenuItem key={item.title}>{renderMenuItem(item)}</SidebarMenuItem>
                          ))}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarGroup>
              )
            })}

          {/* Custom modules (built by the tenant or installed from marketplace) */}
          {customModules && customModules.length > 0 && (
            <SidebarGroup>
              <Collapsible open={!collapsedGroups['Custom modules']} onOpenChange={() => toggleGroup('Custom modules')}>
                <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                  <span>Custom modules</span>
                  {collapsedGroups['Custom modules'] ? (
                    <ChevronRight className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {customModules
                    .filter((mod) => mod.status === 'active' || mod.status === 'paused')
                    .map((mod) => {
                    const to = `/m/${mod.key}`
                    const isActive = pathname.pathname.startsWith(to)
                    const navItems = navItemsByModule.get(mod.key) ?? []
                    const moduleIcon = mod.icon ? (
                      <DynamicIcon
                        name={mod.icon as Parameters<typeof DynamicIcon>[0]['name']}
                        className="h-4 w-4"
                      />
                    ) : (
                      <Box className="h-4 w-4" />
                    )

                    // Modules with custom nav items get a collapsible entry.
                    // Modules without nav items fall back to a simple link
                    // to the module home page.
                    if (navItems.length > 0) {
                      return (
                        <SidebarMenuItem key={mod.id}>
                          <Collapsible defaultOpen={isActive}>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton className="w-full justify-between [&[data-state=open]>svg]:rotate-180">
                                <span className="flex items-center gap-2">
                                  {moduleIcon}
                                  <span>{mod.name}</span>
                                </span>
                                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                              <SidebarMenuSub>
                                {/* Always show a link to the module home at the top */}
                                <SidebarMenuItem>
                                  <SidebarMenuButton
                                    onClick={() => setOpenMobile(!openMobile)}
                                    asChild
                                  >
                                    <Link
                                      to="/m/$moduleKey"
                                      params={{ moduleKey: mod.key }}
                                    >
                                      <span className="text-muted-foreground">Overview</span>
                                    </Link>
                                  </SidebarMenuButton>
                                </SidebarMenuItem>
                                {navItems.map((item) => {
                                  // Only 'entity' nav items have a target route today.
                                  // 'custom_page' is reserved for future work and
                                  // silently skipped here.
                                  if (item.type !== 'entity' || !item.entityKey) return null
                                  return (
                                    <SidebarMenuItem key={item.id}>
                                      <SidebarMenuButton
                                        onClick={() => setOpenMobile(!openMobile)}
                                        asChild
                                      >
                                        <Link
                                          to="/m/$moduleKey/$entityKey"
                                          params={{
                                            moduleKey: mod.key,
                                            entityKey: item.entityKey,
                                          }}
                                        >
                                          {item.icon ? (
                                            <DynamicIcon
                                              name={
                                                item.icon as Parameters<
                                                  typeof DynamicIcon
                                                >[0]['name']
                                              }
                                              className="h-4 w-4"
                                            />
                                          ) : null}
                                          <span>{item.label}</span>
                                        </Link>
                                      </SidebarMenuButton>
                                    </SidebarMenuItem>
                                  )
                                })}
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </Collapsible>
                        </SidebarMenuItem>
                      )
                    }

                    return (
                      <SidebarMenuItem key={mod.id}>
                        <SidebarMenuButton
                          isActive={isActive}
                          onClick={() => setOpenMobile(!openMobile)}
                          asChild
                        >
                          <Link
                            to="/m/$moduleKey"
                            params={{ moduleKey: mod.key }}
                          >
                            {moduleIcon}
                            <span className={mod.status === 'paused' ? 'opacity-60' : ''}>
                              {mod.name}
                            </span>
                            {mod.status === 'paused' && (
                              <Badge variant="outline" className="text-[9px] ml-auto">
                                Paused
                              </Badge>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          )}

          {/* Regular navigation */}
          {navigationsData.map((nav) => {
            // Filter items based on module status
            const filteredItems = filterNavItems(nav.items)

            // Skip rendering if no items are visible
            if (filteredItems.length === 0) {
              return null
            }

            const title = t(`navigation.${titleCaseToCamelCase(nav.title)}`)
            if (
              (nav.title === 'Design System' || nav.title === 'Admin') &&
              !current?.user?.role?.includes('admin')
            ) {
              return null
            }

            const regGroupKey = nav.title
            const regIsCollapsed = collapsedGroups[regGroupKey] ?? false

            return (
              <SidebarGroup key={nav.title}>
                <Collapsible open={!regIsCollapsed} onOpenChange={() => toggleGroup(regGroupKey)}>
                  <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                    <span>{title}</span>
                    {regIsCollapsed ? (
                      <ChevronRight className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredItems.map((item) => (
                      <SidebarMenuItem key={item.title}>{renderMenuItem(item)}</SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarGroup>
            )
          })}
        </SidebarContent>
      </ScrollArea>
    </SidebarWrapper>
  )
}

/**
 * Workspace switcher dropdown — shown only when the user has access
 * to more than one workspace. Clicking another workspace navigates
 * to the ERP sign-in endpoint with a tenantId hint, which creates a
 * new session in the target workspace.
 */
function WorkspaceSwitcher({
  current,
  workspaces,
}: {
  current: { tenantId: string | null; name: string }
  workspaces: Array<{ tenantId: string; name: string; status: string }>
}) {
  const [open, setOpen] = useState(false)

  const [switching, setSwitching] = useState(false)

  async function switchWorkspace(_tenantId: string) {
    if (switching) return
    setSwitching(true)

    const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8787'

    try {
      // Sign out of the current tenant session + clear routing cookies
      await fetch(`${backendUrl}/api/v1/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
      })
      await fetch(`${backendUrl}/api/v1/auth/clear-tenant-cookies`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch {
      // Best-effort — the sign-in page handles stale cookies
    }

    // Redirect to the ERP sign-in page. The user re-enters their
    // credentials and the sign-in flow resolves their workspaces
    // via the erp_user_tenants index. If they have multiple, the
    // workspace picker is shown. Works for all user types — both
    // platform users and ERP-native employees.
    window.location.href = '/login'
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors">
        <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="flex-1 truncate font-medium">{current.name || 'Workspace'}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-0.5">
        {workspaces.map((ws) => {
          const isCurrent = ws.tenantId === current.tenantId
          return (
            <button
              key={ws.tenantId}
              type="button"
              disabled={isCurrent}
              onClick={() => switchWorkspace(ws.tenantId)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent disabled:opacity-60"
            >
              {isCurrent ? (
                <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              ) : (
                <div className="h-3.5 w-3.5 flex-shrink-0" />
              )}
              <span className="flex-1 truncate">{ws.name}</span>
              {ws.status !== 'active' && (
                <span className="text-[10px] text-muted-foreground">
                  {ws.status === 'paused' ? 'paused' : 'deleting'}
                </span>
              )}
            </button>
          )
        })}
      </CollapsibleContent>
    </Collapsible>
  )
}
